/* UID theme — minimal vanilla JS
 * - Quantity steppers
 * - Variant picker (size + color) → AJAX add to cart → opens cart drawer
 * - Cart drawer (slide-in, Section Rendering API for qty/remove)
 * - Collection filters (no-JS form fallback + AJAX enhancement + history.pushState)
 * - Mobile filter drawer (reuses generic drawer helper)
 * - Mix & match: pure CSS via :checked sibling, no JS needed
 */

(function () {
  'use strict';

  /* ==========================================================================
     UTIL: money + cart count
     ========================================================================== */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function updateCartCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  /* ==========================================================================
     DRAWER (generic — reused by cart drawer + mobile filters)
     ========================================================================== */
  let lastFocused = null;

  function getOverlay() {
    let o = document.querySelector('.drawer-overlay');
    if (!o) {
      o = document.createElement('div');
      o.className = 'drawer-overlay';
      o.setAttribute('data-drawer-close', '');
      document.body.appendChild(o);
    }
    return o;
  }

  function openDrawer(drawer) {
    if (!drawer) return;
    lastFocused = document.activeElement;
    const overlay = getOverlay();
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.classList.add('is-drawer-open');
    drawer.setAttribute('aria-hidden', 'false');
    const focusable = drawer.querySelector('[data-drawer-close], button, [href], input, select');
    if (focusable) focusable.focus();
  }

  function closeDrawer(drawer) {
    if (!drawer) drawer = document.querySelector('.drawer.is-open');
    if (!drawer) return;
    const overlay = document.querySelector('.drawer-overlay');
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('is-drawer-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  // Open triggers
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-drawer-open]');
    if (trigger) {
      e.preventDefault();
      const id = trigger.getAttribute('data-drawer-open');
      const drawer = document.getElementById(id);
      openDrawer(drawer);
      return;
    }
    if (e.target.closest('[data-drawer-close]')) {
      e.preventDefault();
      closeDrawer();
    }
  });

  // ESC closes
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ==========================================================================
     QUANTITY STEPPERS (PDP form)
     ========================================================================== */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-qty-step]');
    if (!btn) return;
    const wrap = btn.closest('.qty-row');
    if (!wrap) return;
    const input = wrap.querySelector('.qty-input');
    if (!input) return;
    const delta = parseInt(btn.getAttribute('data-qty-step'), 10);
    let v = parseInt(input.value, 10) + delta;
    if (isNaN(v) || v < 1) v = 1;
    if (v > 99) v = 99;
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ==========================================================================
     VARIANT PICKER + AJAX ADD TO CART
     ========================================================================== */
  document.querySelectorAll('[data-variant-form]').forEach(initVariantForm);

  function initVariantForm(form) {
    const dataEl = form.querySelector('[data-product-json]');
    if (!dataEl) return;

    let product;
    try {
      product = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.warn('UID: bad product JSON', err);
      return;
    }

    const idInput = form.querySelector('[name="id"]');
    const priceEl = form.querySelector('[data-price-now]');
    const compareEl = form.querySelector('[data-price-was]');
    const submitBtn = form.querySelector('[data-add-to-cart]');
    const submitLabel = submitBtn ? submitBtn.querySelector('[data-add-to-cart-text]') : null;
    // Mobile sticky button lives outside the form, linked via form="..." attribute.
    // It mirrors the main submit button's disabled/label state.
    const stickyBtn = form.id ? document.querySelector('[data-pdp-sticky-add][form="' + form.id + '"]') : null;
    const stickyLabel = stickyBtn ? stickyBtn.querySelector('[data-pdp-sticky-text]') : null;

    function selectedOptions() {
      const opts = [];
      product.options.forEach(function (_, i) {
        const sel = form.querySelector('input[type=radio][data-option-index="' + i + '"]:checked');
        opts[i] = sel ? sel.value : null;
      });
      return opts;
    }

    function findVariant(opts) {
      return product.variants.find(function (v) {
        return v.options.every(function (val, i) { return val === opts[i]; });
      });
    }

    function update() {
      const opts = selectedOptions();
      if (opts.some(function (o) { return o == null; })) return;

      const v = findVariant(opts);
      if (!v) return;

      idInput.value = v.id;
      if (priceEl) priceEl.textContent = formatMoney(v.price);
      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.textContent = formatMoney(v.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (submitBtn) {
        submitBtn.disabled = !v.available;
        if (submitLabel) {
          submitLabel.textContent = v.available
            ? (submitBtn.getAttribute('data-label-add') || 'Get both sets')
            : (submitBtn.getAttribute('data-label-soldout') || 'Sold out');
        }
      }
      if (stickyBtn) {
        stickyBtn.disabled = !v.available;
        if (stickyLabel) {
          stickyLabel.textContent = v.available
            ? ((stickyBtn.getAttribute('data-label-add') || 'Get both sets') + ' →')
            : (stickyBtn.getAttribute('data-label-soldout') || 'Sold out');
        }
      }
    }

    form.querySelectorAll('input[type=radio][data-option-index]').forEach(function (r) {
      r.addEventListener('change', update);
    });
    update();

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitBtn) submitBtn.disabled = true;
      if (stickyBtn) stickyBtn.disabled = true;

      const fd = new FormData(form);
      // Section Rendering API: ask Shopify to return the cart drawer HTML
      fd.append('sections', 'cart-drawer');

      fetch('/cart/add.js', {
        method: 'POST',
        body: fd,
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function (data) {
          // Update cart count
          fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
            updateCartCount(cart.item_count);
          });
          // Re-render drawer from sections payload
          if (data.sections && data.sections['cart-drawer']) {
            replaceDrawerHTML(data.sections['cart-drawer']);
          }
          openCartDrawer();
          if (submitBtn) submitBtn.disabled = false;
          if (stickyBtn) stickyBtn.disabled = false;
        })
        .catch(function () {
          if (submitBtn) submitBtn.disabled = false;
          if (stickyBtn) stickyBtn.disabled = false;
          window.location.href = '/cart';
        });
    });
  }

  /* ==========================================================================
     CART DRAWER
     ========================================================================== */
  function getCartDrawer() {
    return document.getElementById('cart-drawer');
  }

  function openCartDrawer() {
    openDrawer(getCartDrawer());
  }

  // Header cart link → open drawer (keeps href fallback for no-JS)
  document.addEventListener('click', function (e) {
    const link = e.target.closest('[data-cart-toggle]');
    if (!link) return;
    e.preventDefault();
    openCartDrawer();
  });

  // Replace cart drawer HTML returned by Section Rendering API.
  // The API returns the raw section markup; we need to swap the inner contents
  // (everything inside the .shopify-section wrapper).
  function replaceDrawerHTML(html) {
    const drawer = getCartDrawer();
    if (!drawer) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const newDrawer = tmp.querySelector('#cart-drawer');
    if (newDrawer) {
      drawer.innerHTML = newDrawer.innerHTML;
      if (drawer.classList.contains('is-open')) {
        drawer.setAttribute('aria-hidden', 'false');
      }
    }
    loadCartRecommendations();
  }

  /* -------------------- Cart recommendations (cached by product_id) -------------------- */
  let lastRecsProductId = null;
  let cachedRecsHTML = null;

  function loadCartRecommendations() {
    const slot = document.querySelector('#cart-drawer [data-recommendations]');
    if (!slot) {
      lastRecsProductId = null;
      cachedRecsHTML = null;
      return;
    }
    const productId = slot.getAttribute('data-product-id');
    if (!productId) return;

    // Already loaded for this anchor → reuse cached HTML, don't refetch
    if (productId === lastRecsProductId && cachedRecsHTML) {
      slot.innerHTML = cachedRecsHTML;
      return;
    }

    const url = '/recommendations/products' +
      '?section_id=cart-recommendations' +
      '&product_id=' + encodeURIComponent(productId) +
      '&limit=4&intent=related';

    fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        const tmp = document.createElement('div');
        tmp.innerHTML = html.trim();
        const inner = tmp.firstElementChild ? tmp.firstElementChild.innerHTML : html;
        if (inner.trim()) {
          slot.innerHTML = inner;
          lastRecsProductId = productId;
          cachedRecsHTML = inner;
        }
      })
      .catch(function () { /* silent — recs are non-critical */ });
  }

  // Initial load (page may already render with cart items)
  loadCartRecommendations();

  /* ==========================================================================
     CUSTOM SET BUILDER
     ========================================================================== */
  document.querySelectorAll('[data-csb]').forEach(initSetBuilder);
  document.querySelectorAll('[data-pys]').forEach(initPickYourSet);

  function initSetBuilder(root) {
    const dataEl = root.querySelector('[data-csb-data]');
    if (!dataEl) return;

    let data;
    try { data = JSON.parse(dataEl.textContent); }
    catch (err) { console.warn('CSB: bad JSON', err); return; }

    const state = {
      legging: { product: null, variant: null },
      bra:     { product: null, variant: null }
    };

    function setStatus(piece, text, ready) {
      const panel = root.querySelector('[data-piece="' + piece + '"]');
      const status = panel && panel.querySelector('[data-piece-status]');
      if (status) {
        status.textContent = text;
        status.classList.toggle('is-ready', !!ready);
      }
    }

    function renderSizes(piece) {
      const panel = root.querySelector('[data-piece="' + piece + '"]');
      const wrap = panel.querySelector('[data-sizes]');
      const label = panel.querySelector('[data-sizes-label]');
      const product = state[piece].product;

      if (!product) {
        wrap.innerHTML = '';
        if (label) label.hidden = true;
        return;
      }

      if (label) label.hidden = false;
      wrap.innerHTML = product.variants.map(function (v) {
        const disabled = v.available ? '' : 'disabled';
        const cls = v.available ? '' : ' is-soldout';
        return '<button type="button" class="csb__size' + cls + '" data-size="' + v.size +
               '" data-variant-id="' + v.id + '" role="radio" aria-checked="false" ' + disabled + '>' +
               v.size + '</button>';
      }).join('');
    }

    function renderPreview() {
      ['legging', 'bra'].forEach(function (piece) {
        const slot = root.querySelector('[data-preview="' + piece + '"]');
        const product = state[piece].product;
        if (product) {
          slot.innerHTML = '<img src="' + product.image + '" alt="' + product.color + '">';
        } else {
          slot.innerHTML = '<span class="csb__preview-placeholder">' +
                           (piece === 'legging' ? 'Legging' : 'Bra') + '</span>';
        }
      });
    }

    function formatPrice(cents) {
      return '$' + (cents / 100).toFixed(0).replace(/\.0+$/, '');
    }

    function updateTotals() {
      const lv = state.legging.variant;
      const bv = state.bra.variant;
      const ready = lv && bv;

      const totalEl = root.querySelector('[data-total-price]');
      const stickyPrice = root.querySelector('[data-sticky-price]');
      const savingsRow = root.querySelector('[data-savings]');
      const regularEl = root.querySelector('[data-regular-price]');
      const saveTagEl = root.querySelector('[data-save-tag]');
      const addBtn = root.querySelector('[data-csb-add]');
      const addText = root.querySelector('[data-csb-add-text]');
      const addBtnMobile = root.querySelector('[data-csb-add-mobile]');
      const addTextMobile = root.querySelector('[data-csb-add-mobile-text]');
      const sticky = root.querySelector('[data-csb-sticky]');

      if (sticky) sticky.hidden = false;

      if (!ready) {
        const placeholder = '—';
        if (totalEl) totalEl.textContent = placeholder;
        if (stickyPrice) stickyPrice.textContent = placeholder;
        if (savingsRow) savingsRow.hidden = true;
        if (addBtn) {
          addBtn.disabled = true;
          if (addText) addText.textContent = addBtn.getAttribute('data-label-incomplete') || 'Pick Color + Size';
        }
        if (addBtnMobile) {
          addBtnMobile.disabled = true;
          if (addTextMobile) addTextMobile.textContent = 'Pick Color + Size';
        }
        return;
      }

      const sumPrice = lv.price + bv.price;
      const sumCompare = (lv.compare_at_price || lv.price) + (bv.compare_at_price || bv.price);
      const bundlePrice = data.enable_bundle && data.bundle_price > 0 ? data.bundle_price : sumPrice;
      const referencePrice = sumCompare > sumPrice ? sumCompare : sumPrice;
      const savings = referencePrice - bundlePrice;

      if (totalEl) totalEl.textContent = formatPrice(bundlePrice);
      if (stickyPrice) stickyPrice.textContent = formatPrice(bundlePrice);

      if (savingsRow) {
        if (savings > 0) {
          if (regularEl) regularEl.textContent = formatPrice(referencePrice);
          if (saveTagEl) saveTagEl.textContent = 'Save ' + formatPrice(savings);
          savingsRow.hidden = false;
        } else {
          savingsRow.hidden = true;
        }
      }

      const ctaLabel = addBtn ? (addBtn.getAttribute('data-label-add') || 'Add Both to Cart') : 'Add Both to Cart';
      if (addBtn) {
        addBtn.disabled = false;
        if (addText) addText.textContent = ctaLabel + ' →';
      }
      if (addBtnMobile) {
        addBtnMobile.disabled = false;
        if (addTextMobile) addTextMobile.textContent = 'Add to Cart →';
      }
    }

    function selectColor(piece, productId) {
      const product = data.pieces[piece].find(function (p) { return p.id === productId; });
      if (!product) return;
      state[piece].product = product;
      state[piece].variant = null;

      // Update color button states
      root.querySelectorAll('[data-piece="' + piece + '"] [data-color]').forEach(function (btn) {
        const isActive = parseInt(btn.getAttribute('data-product-id'), 10) === productId;
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      setStatus(piece, product.color + ' · pick a size', false);
      renderSizes(piece);
      renderPreview();
      updateTotals();
    }

    function selectSize(piece, variantId) {
      const product = state[piece].product;
      if (!product) return;
      const variant = product.variants.find(function (v) { return v.id === variantId; });
      if (!variant || !variant.available) return;

      state[piece].variant = variant;

      root.querySelectorAll('[data-piece="' + piece + '"] [data-size]').forEach(function (btn) {
        const isActive = parseInt(btn.getAttribute('data-variant-id'), 10) === variantId;
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      });

      setStatus(piece, product.color + ' · ' + variant.size + ' ✓', true);
      updateTotals();
    }

    // Click handlers
    root.addEventListener('click', function (e) {
      const colorBtn = e.target.closest('[data-color]');
      if (colorBtn && !colorBtn.classList.contains('is-soldout')) {
        const panel = colorBtn.closest('[data-piece]');
        if (!panel) return;
        const piece = panel.getAttribute('data-piece');
        const productId = parseInt(colorBtn.getAttribute('data-product-id'), 10);
        selectColor(piece, productId);
        return;
      }

      const sizeBtn = e.target.closest('[data-size]');
      if (sizeBtn && !sizeBtn.disabled) {
        const panel = sizeBtn.closest('[data-piece]');
        if (!panel) return;
        const piece = panel.getAttribute('data-piece');
        const variantId = parseInt(sizeBtn.getAttribute('data-variant-id'), 10);
        selectSize(piece, variantId);
        return;
      }

      const addBtn = e.target.closest('[data-csb-add], [data-csb-add-mobile]');
      if (addBtn && !addBtn.disabled) {
        addBundleToCart(addBtn);
      }
    });

    function addBundleToCart(btn) {
      const lv = state.legging.variant;
      const bv = state.bra.variant;
      if (!lv || !bv) return;

      btn.disabled = true;
      const allBtns = root.querySelectorAll('[data-csb-add], [data-csb-add-mobile]');
      allBtns.forEach(function (b) { b.disabled = true; });

      const body = {
        items: [
          { id: lv.id, quantity: 1 },
          { id: bv.id, quantity: 1 }
        ],
        sections: 'cart-drawer'
      };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function (response) {
          if (response.sections && response.sections['cart-drawer']) {
            replaceDrawerHTML(response.sections['cart-drawer']);
          }
          return fetch('/cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          openCartDrawer();
          allBtns.forEach(function (b) { b.disabled = false; });
        })
        .catch(function () {
          allBtns.forEach(function (b) { b.disabled = false; });
          window.location.href = '/cart';
        });
    }
  }

  /* ==========================================================================
     PICK YOUR SET — multi-step wizard
     ========================================================================== */
  function initPickYourSet(root) {
    const dataEl = root.querySelector('[data-pys-data]');
    if (!dataEl) return;

    let data;
    try { data = JSON.parse(dataEl.textContent); }
    catch (err) { console.warn('PYS: bad JSON', err); return; }

    const state = {
      step: 1,
      legging: { product: null, variant: null },
      bra:     { product: null, variant: null }
    };

    const track = root.querySelector('[data-pys-track]');
    const totalSteps = 3;

    function formatPrice(cents) {
      return '$' + (cents / 100).toFixed(0).replace(/\.0+$/, '');
    }

    function isStepComplete(n) {
      if (n === 1) return !!state.legging.variant;
      if (n === 2) return !!state.bra.variant;
      return false;
    }

    function maxReachableStep() {
      // Step 1 always reachable. Step 2 needs step 1 done. Step 3 needs both.
      if (!isStepComplete(1)) return 1;
      if (!isStepComplete(2)) return 2;
      return 3;
    }

    function goToStep(n) {
      const max = maxReachableStep();
      if (n > max) return;

      state.step = n;
      track.style.transform = 'translateX(-' + ((n - 1) * 100) + '%)';

      // Active step
      root.querySelectorAll('[data-step]').forEach(function (el) {
        const i = parseInt(el.getAttribute('data-step'), 10);
        const isActive = i === n;
        el.classList.toggle('is-active', isActive);
        el.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      // Progress dots
      root.querySelectorAll('[data-progress]').forEach(function (dot) {
        const i = parseInt(dot.getAttribute('data-progress'), 10);
        dot.classList.toggle('is-active', i === n);
        dot.classList.toggle('is-done', i < n && isStepComplete(i));
        dot.classList.toggle('is-clickable', i <= max);
        dot.setAttribute('aria-current', i === n ? 'step' : 'false');
      });

      // Progress lines
      root.querySelectorAll('.pys__step-line').forEach(function (line, idx) {
        // line idx 0 connects step 1→2, idx 1 connects 2→3
        line.classList.toggle('is-done', idx + 1 < n && isStepComplete(idx + 1));
      });

      // Sticky bar only relevant on step 3
      const sticky = root.querySelector('[data-pys-sticky]');
      if (sticky) sticky.hidden = (n !== 3);

      // On step 3, render summary
      if (n === 3) renderSummary();

      // Smooth scroll the section into view if user is far below
      try {
        const rect = root.getBoundingClientRect();
        if (rect.top < -100 || rect.top > window.innerHeight) {
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (_) {}
    }

    function renderSizes(piece) {
      const wrap = root.querySelector('[data-sizes][data-piece="' + piece + '"]');
      const label = root.querySelector('[data-sizes-label][data-piece="' + piece + '"]');
      const product = state[piece].product;

      if (!product) {
        wrap.innerHTML = '';
        if (label) label.hidden = true;
        return;
      }

      if (label) label.hidden = false;
      wrap.innerHTML = product.variants.map(function (v) {
        const disabled = v.available ? '' : 'disabled';
        const cls = v.available ? '' : ' is-soldout';
        return '<button type="button" class="csb__size' + cls + '" data-size="' + v.size +
               '" data-variant-id="' + v.id + '" data-piece="' + piece +
               '" role="radio" aria-checked="false" ' + disabled + '>' +
               v.size + '</button>';
      }).join('');
    }

    function selectColor(piece, productId) {
      const product = data.pieces[piece].find(function (p) { return p.id === productId; });
      if (!product) return;

      state[piece].product = product;
      state[piece].variant = null;

      root.querySelectorAll('[data-color][data-piece="' + piece + '"]').forEach(function (btn) {
        btn.setAttribute('aria-checked',
          parseInt(btn.getAttribute('data-product-id'), 10) === productId ? 'true' : 'false');
      });

      renderSizes(piece);
      updateNextButtons();
    }

    function selectSize(piece, variantId) {
      const product = state[piece].product;
      if (!product) return;
      const variant = product.variants.find(function (v) { return v.id === variantId; });
      if (!variant || !variant.available) return;

      state[piece].variant = variant;

      root.querySelectorAll('[data-size][data-piece="' + piece + '"]').forEach(function (btn) {
        btn.setAttribute('aria-checked',
          parseInt(btn.getAttribute('data-variant-id'), 10) === variantId ? 'true' : 'false');
      });

      updateNextButtons();
    }

    function updateNextButtons() {
      // Step 1 next: enabled when legging variant chosen
      const next1 = root.querySelector('[data-pys-next="2"]');
      if (next1) next1.disabled = !isStepComplete(1);

      // Step 2 next: enabled when bra variant chosen
      const next2 = root.querySelector('[data-pys-next="3"]');
      if (next2) next2.disabled = !isStepComplete(2);
    }

    function renderSummary() {
      // Images
      ['legging', 'bra'].forEach(function (piece) {
        const slot = root.querySelector('[data-summary-img="' + piece + '"]');
        const product = state[piece].product;
        if (slot && product) {
          slot.innerHTML = '<img src="' + product.image + '" alt="' + product.color + '">';
        }
      });

      // Detail lines
      ['legging', 'bra'].forEach(function (piece) {
        const detail = root.querySelector('[data-summary-detail="' + piece + '"]');
        const v = state[piece].variant;
        const p = state[piece].product;
        if (detail && v && p) {
          detail.textContent = p.color + ' · ' + v.size;
        }
      });

      // Pricing
      const lv = state.legging.variant;
      const bv = state.bra.variant;
      if (!lv || !bv) return;

      const sumPrice = lv.price + bv.price;
      const sumCompare = (lv.compare_at_price || lv.price) + (bv.compare_at_price || bv.price);
      const bundlePrice = data.enable_bundle && data.bundle_price > 0 ? data.bundle_price : sumPrice;
      const referencePrice = sumCompare > sumPrice ? sumCompare : sumPrice;
      const savings = referencePrice - bundlePrice;

      const totalEl = root.querySelector('[data-total-price]');
      const stickyPrice = root.querySelector('[data-sticky-price]');
      const savingsRow = root.querySelector('[data-savings]');
      const regularEl = root.querySelector('[data-regular-price]');
      const saveTagEl = root.querySelector('[data-save-tag]');

      if (totalEl) totalEl.textContent = formatPrice(bundlePrice);
      if (stickyPrice) stickyPrice.textContent = formatPrice(bundlePrice);

      if (savingsRow) {
        if (savings > 0) {
          if (regularEl) regularEl.textContent = formatPrice(referencePrice);
          if (saveTagEl) saveTagEl.textContent = 'Save ' + formatPrice(savings);
          savingsRow.hidden = false;
        } else {
          savingsRow.hidden = true;
        }
      }

      // Enable add buttons
      root.querySelectorAll('[data-pys-add], [data-pys-add-mobile]').forEach(function (b) {
        b.disabled = false;
      });
    }

    function addBundleToCart(btn) {
      const lv = state.legging.variant;
      const bv = state.bra.variant;
      if (!lv || !bv) return;

      const allBtns = root.querySelectorAll('[data-pys-add], [data-pys-add-mobile]');
      allBtns.forEach(function (b) { b.disabled = true; });

      const labels = root.querySelectorAll('[data-pys-add-text], [data-pys-add-mobile-text]');
      labels.forEach(function (l) { l.textContent = 'Adding…'; });

      const body = {
        items: [
          { id: lv.id, quantity: 1 },
          { id: bv.id, quantity: 1 }
        ],
        sections: 'cart-drawer'
      };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function (response) {
          if (response.sections && response.sections['cart-drawer']) {
            replaceDrawerHTML(response.sections['cart-drawer']);
          }
          return fetch('/cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          openCartDrawer();
          allBtns.forEach(function (b) { b.disabled = false; });
          labels.forEach(function (l) { l.textContent = 'Added ✓'; });
          setTimeout(function () {
            labels.forEach(function (l, i) {
              l.textContent = i === 0
                ? (root.querySelector('[data-pys-add]').getAttribute('data-label-add') || 'Add Both to Cart') + ' →'
                : 'Add to Cart →';
            });
          }, 1400);
        })
        .catch(function () {
          allBtns.forEach(function (b) { b.disabled = false; });
          labels.forEach(function (l) { l.textContent = 'Add Both to Cart →'; });
          window.location.href = '/cart';
        });
    }

    /* -------- Click handlers -------- */
    root.addEventListener('click', function (e) {
      // Color selection
      const colorBtn = e.target.closest('[data-color]');
      if (colorBtn) {
        const piece = colorBtn.getAttribute('data-piece');
        const productId = parseInt(colorBtn.getAttribute('data-product-id'), 10);
        selectColor(piece, productId);
        return;
      }

      // Size selection
      const sizeBtn = e.target.closest('[data-size]');
      if (sizeBtn && !sizeBtn.disabled) {
        const piece = sizeBtn.getAttribute('data-piece');
        const variantId = parseInt(sizeBtn.getAttribute('data-variant-id'), 10);
        selectSize(piece, variantId);
        return;
      }

      // Step navigation
      const next = e.target.closest('[data-pys-next]');
      if (next && !next.disabled) {
        goToStep(parseInt(next.getAttribute('data-pys-next'), 10));
        return;
      }
      const prev = e.target.closest('[data-pys-prev]');
      if (prev) {
        goToStep(parseInt(prev.getAttribute('data-pys-prev'), 10));
        return;
      }

      // Progress dot — jump to a previously-visited step
      const dot = e.target.closest('[data-progress]');
      if (dot && dot.classList.contains('is-clickable')) {
        goToStep(parseInt(dot.getAttribute('data-progress'), 10));
        return;
      }

      // Add to cart
      const addBtn = e.target.closest('[data-pys-add], [data-pys-add-mobile]');
      if (addBtn && !addBtn.disabled) {
        addBundleToCart(addBtn);
      }
    });

    // Keyboard support: Enter on progress dots
    root.querySelectorAll('[data-progress]').forEach(function (dot) {
      dot.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (dot.classList.contains('is-clickable')) {
            goToStep(parseInt(dot.getAttribute('data-progress'), 10));
          }
        }
      });
    });

    // Initial state
    goToStep(1);
  }

  // Cart drawer line qty + remove
  document.addEventListener('click', function (e) {
    const drawer = e.target.closest('#cart-drawer');
    if (!drawer) return;

    const btn = e.target.closest('[data-cart-change]');
    if (!btn) return;

    e.preventDefault();
    const lineKey = btn.getAttribute('data-cart-change');
    const newQty = parseInt(btn.getAttribute('data-cart-qty'), 10);
    if (isNaN(newQty) || !lineKey) return;

    drawer.style.opacity = '0.6';
    drawer.style.pointerEvents = 'none';

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: newQty, sections: 'cart-drawer' })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        updateCartCount(data.item_count);
        if (data.sections && data.sections['cart-drawer']) {
          replaceDrawerHTML(data.sections['cart-drawer']);
        }
      })
      .finally(function () {
        drawer.style.opacity = '';
        drawer.style.pointerEvents = '';
      });
  });

  /* ==========================================================================
     CART PAGE: legacy form submit on qty input change
     ========================================================================== */
  document.querySelectorAll('[data-cart-form]').forEach(function (form) {
    form.addEventListener('change', function (e) {
      const input = e.target.closest('input[name^="updates"]');
      if (!input) return;
      form.submit();
    });
  });

  /* ==========================================================================
     COLLECTION FILTERS — AJAX enhancement
     ========================================================================== */
  let filterPending = null;
  function submitFiltersFromForm(form) {
    if (filterPending) clearTimeout(filterPending);
    filterPending = setTimeout(function () {
      const fd = new FormData(form);
      const params = new URLSearchParams();
      for (const [key, val] of fd.entries()) {
        if (val !== '' && val != null) params.append(key, val);
      }
      const url = form.getAttribute('action') + '?' + params.toString();

      history.replaceState({}, '', url);

      fetch(url + '&sections=main-collection', {
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          const html = data['main-collection'];
          if (!html) return;
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          ['[data-collection-grid]', '[data-active-filters]', '[data-collection-count]'].forEach(function (sel) {
            const newEl = tmp.querySelector(sel);
            const oldEl = document.querySelector(sel);
            if (newEl && oldEl) oldEl.innerHTML = newEl.innerHTML;
          });
          // Sync the OTHER filter form (desktop ↔ mobile drawer) so checkboxes stay aligned
          document.querySelectorAll('[data-filter-form]').forEach(function (otherForm) {
            if (otherForm === form) return;
            const newOther = tmp.querySelector('[data-filter-form]');
            if (newOther) otherForm.innerHTML = newOther.innerHTML;
          });
        })
        .catch(function () {
          window.location.href = url;
        });
    }, 200);
  }

  function initFilterForm(form) {
    if (!form) return;

    form.addEventListener('change', function (e) {
      if (e.target.matches('input, select')) submitFiltersFromForm(form);
    });

    // Intercept Enter key in price-range inputs
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submitFiltersFromForm(form);
    });
  }

  document.querySelectorAll('[data-filter-form]').forEach(initFilterForm);

  // Sort dropdown lives outside the form — submit via the (first) filter form
  document.querySelectorAll('[data-sort-by]').forEach(function (sel) {
    sel.addEventListener('change', function () {
      const form = document.querySelector('[data-filter-form]');
      if (!form) {
        // No filter form on this page — just reload with sort param
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sel.value);
        window.location.href = url.toString();
        return;
      }
      let hidden = form.querySelector('input[name="sort_by"]');
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'sort_by';
        form.appendChild(hidden);
      }
      hidden.value = sel.value;
      submitFiltersFromForm(form);
    });
  });

  // Restore page on back/forward
  window.addEventListener('popstate', function () {
    window.location.reload();
  });

  /* ==========================================================================
     CTA TRACKING — auto-fires gtag('event','cta_click',...) when GA4 is loaded
     ==========================================================================
     Every clickable CTA across the theme carries:
       data-cta="<unique-slug>"      e.g. "hero-primary", "pys-add-bundle"
       data-section="<section-id>"   e.g. "hero", "pick-your-set"
       data-product-handle="..."     when applicable
     If GA4 / GTM isn't loaded, this is a no-op.
     ========================================================================== */
  document.addEventListener('click', function (e) {
    const el = e.target.closest('[data-cta]');
    if (!el) return;

    const payload = {
      cta: el.getAttribute('data-cta'),
      section: el.getAttribute('data-section') || null,
      label: (el.textContent || '').trim().slice(0, 80),
      href: el.getAttribute('href') || null,
      product_handle: el.getAttribute('data-product-handle') || null
    };

    // GA4 (gtag.js) — direct
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'cta_click', payload);
    }

    // GTM dataLayer — works if Tag Manager is configured to listen for cta_click
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({ event: 'cta_click' }, payload));
    }

    // Also expose a custom DOM event for any third-party listener
    document.dispatchEvent(new CustomEvent('uid:cta_click', { detail: payload }));
  });

})();
